import { notification } from 'antd';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import {
  type ClientFormValues,
  type OtpFormValues
} from '@/components/Client/AutoCredit/shared/entities/client-otp-modal';
import { clientApi, clientCreateApi } from '@/components/Client/api';
import { authValidateOtp } from '@/components/Client/client.store';
import type { ModalOpenerType } from '@/components/Shared/entities/client-otp-modal';
import {
  CLIENT_ROUTES,
  getFullClientRoute
} from '@/components/Shared/routes/routes';
import useAppNavigate from '@/hooks/useAppNavigation';
import { gaEvent } from '@/lib/google-analytics';
import { handleError } from '@/services/handleError';
import { useAppDispatch } from '@/store';

interface IProps {
  setShowOtpModal: (value: ModalOpenerType) => void;
}

const useAuth = ({ setShowOtpModal }: IProps) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const { navigate } = useAppNavigate();

  const [clientInfo, setClientInfo] = useState<ClientFormValues | null>(null);

  const handleCreateOtp = async (data: ClientFormValues) => {
    try {
      const phone = data.phone.replace(/\D/g, '');
      data.phone = phone;
      await clientApi.authCreateOtp({ iin: data.iin, phoneNumber: data.phone });
      setClientInfo(data);
      setShowOtpModal('otp-form');
      return '';
    } catch (error) {
      let errorMessage = '';
      handleError(error, {
        onError: (e) => {
          errorMessage = e.message;
        }
      });
      return errorMessage;
    }
  };

  const handleValidateOtp = async (data: OtpFormValues) => {
    if (!clientInfo) return '';
    const code = data.first + data.second + data.third + data.fourth;
    const response = await dispatch(
      authValidateOtp({
        iin: clientInfo.iin,
        phoneNumber: clientInfo.phone,
        otp: code
      })
    );

    if (authValidateOtp.rejected.match(response)) {
      return response.error.message || t('otp-validation.error');
    }

    setShowOtpModal('');
    navigate(getFullClientRoute(CLIENT_ROUTES.autoCredit) + '?tab=active');
    gaEvent('auth');
    return '';
  };

  const handleResendOtp = async () => {
    if (!clientInfo) {
      notification.error({ title: 'Произошла ошибка! Попробуйте позже' });
      return;
    }
    try {
      await clientCreateApi.resendValidationOtp({
        iin: clientInfo.iin,
        phoneNumber: clientInfo.phone
      });
      notification.success({ title: 'Код успешно отправлен' });
    } catch (error) {
      handleError(error);
    }
  };

  return { handleCreateOtp, handleValidateOtp, handleResendOtp };
};

export default useAuth;
